import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import {
  ApiError,
  probeStallPageAnonymous,
  type AgentType,
  type AuthChallenge,
  type ConsoleStall,
} from '../api/client';

/**
 * Tenant onboarding — the console's front door (PR #115 spec).
 *
 * Five steps, one surface, resumable:
 *   [1] Identity → [2] Stall → [3] Certify + Go live → [4] Fund → [5] Verify
 *
 * The two constraints that shape everything here:
 *
 * KEY CUSTODY (spec §3): Path A's register response carries a private key
 * that exists in exactly one copy. It lives in ONE state variable in this
 * component, renders exactly once behind a blocking acknowledgement, is
 * cleared from state the moment the operator confirms saving it, and never
 * touches storage, URLs, logs, or any host but AEXgora. Path B never asks
 * for a key at all — the tenant signs a challenge on their own machine and
 * pastes the signature.
 *
 * NO SILENT VISIBILITY DEFAULT (spec §4, founder ruling 2026-08-13):
 * public/private is a required explicit choice — two labelled options,
 * neither pre-selected. The verify step then proves the choice landed: a
 * public stall must answer 200 to an unauthenticated GET …/page; a private
 * one must not.
 *
 * The verify step is the console face of mint-verify.mjs (AEXgora #239):
 * the same done-check semantics — exists · certified · live · agent bound ·
 * visibility as chosen · funded — run against browser-reachable surfaces,
 * ✓/✗ per item, red until all green.
 */

type Step = 'identity' | 'key' | 'stall' | 'lifecycle' | 'fund' | 'verify';

type IdentityMode = 'generate' | 'existing' | 'proof';

interface OwnedAgent {
  agent_id: string;
  display_name: string;
}

interface CreatedAgent {
  agent_id: string;
  aex_id: string | null;
  display_name: string;
}

interface CreatedStall {
  stall_id: string;
  name: string;
  is_private: boolean;
}

interface VerifyItem {
  label: string;
  /** true = ✓, false = ✗, null = not applicable / left for later */
  ok: boolean | null;
  detail: string;
}

export interface TenantOnboardingFlowProps {
  onClose: () => void;
  /** Called after any server-side mutation so the Stalls tab can refresh. */
  onMutated: () => void;
  ownedAgents: OwnedAgent[];
  /**
   * Resume path (spec §2): a half-finished tenant is an uncertified or
   * not-live stall. Entering with a stall NEVER re-runs step 1 — a second
   * generate_identity mints a second orphan agent.
   */
  resumeStall?: ConsoleStall | null;
}

const STEP_LABELS: Record<Step, string> = {
  identity: '1 · Identity',
  key: '1 · Identity',
  stall: '2 · Stall',
  lifecycle: '3 · Certify & go live',
  fund: '4 · Fund',
  verify: '5 · Verify',
};

const STEP_ORDER: Step[] = ['identity', 'stall', 'lifecycle', 'fund', 'verify'];

function stepIndex(step: Step): number {
  if (step === 'key') return 0;
  return STEP_ORDER.indexOf(step);
}

const AGENT_TYPES: AgentType[] = ['standard', 'autonomous', 'human_operated', 'hybrid', 'ergaster'];

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : 'Request failed';
}

function parseDomains(raw: string): string[] {
  return raw
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter((d) => d.length > 0);
}

const TenantOnboardingFlow: React.FC<TenantOnboardingFlowProps> = ({
  onClose,
  onMutated,
  ownedAgents,
  resumeStall,
}) => {
  const { api, operatorId } = useOperatorAuth();

  const [step, setStep] = useState<Step>(() => {
    if (!resumeStall) return 'identity';
    if (!resumeStall.is_certified || !resumeStall.is_live) return 'lifecycle';
    return 'fund';
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Step 1: identity ----------------------------------------------------
  const [identityMode, setIdentityMode] = useState<IdentityMode>('generate');
  const [genName, setGenName] = useState('');
  const [genType, setGenType] = useState<AgentType>('standard');
  const [genDescription, setGenDescription] = useState('');
  const [genDomains, setGenDomains] = useState('');
  const [existingAgentId, setExistingAgentId] = useState('');
  // Path B (proof) fields
  const [proofAexId, setProofAexId] = useState('');
  const [proofSignature, setProofSignature] = useState('');
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [challengeBusy, setChallengeBusy] = useState(false);

  const [agent, setAgent] = useState<CreatedAgent | null>(() =>
    resumeStall?.agent_id
      ? { agent_id: resumeStall.agent_id, aex_id: null, display_name: '' }
      : null,
  );

  // The one copy of the Path A private key. Never leaves this variable;
  // cleared on acknowledgement and on unmount. Do not add it to any
  // effect, log, storage call, or error path.
  const [mintedKey, setMintedKey] = useState<string | null>(null);
  const [keyAcked, setKeyAcked] = useState(false);
  useEffect(() => () => setMintedKey(null), []);

  // ---- Step 2: stall -------------------------------------------------------
  const [stallName, setStallName] = useState('');
  const [stallDescription, setStallDescription] = useState('');
  // null until the operator picks one — the submit stays disabled and no
  // path through this UI creates a stall without an explicit choice.
  const [visibility, setVisibility] = useState<'public' | 'private' | null>(() =>
    resumeStall ? (resumeStall.is_private ? 'private' : 'public') : null,
  );
  const [stall, setStall] = useState<CreatedStall | null>(() =>
    resumeStall
      ? { stall_id: resumeStall.stall_id, name: resumeStall.name, is_private: resumeStall.is_private }
      : null,
  );

  // ---- Step 3: lifecycle ---------------------------------------------------
  const [certified, setCertified] = useState(resumeStall?.is_certified ?? false);
  const [live, setLive] = useState(resumeStall?.is_live ?? false);

  // ---- Step 4: fund --------------------------------------------------------
  const [fundAmount, setFundAmount] = useState('');
  const [fundSkipped, setFundSkipped] = useState(false);

  // ---- Step 5: verify ------------------------------------------------------
  const [verifyItems, setVerifyItems] = useState<VerifyItem[] | null>(null);
  const [verifyRunning, setVerifyRunning] = useState(false);
  const verifyRan = useRef(false);

  // ---- Actions -------------------------------------------------------------

  const submitGenerate = async () => {
    if (!operatorId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.registerAgentGenerate({
        display_name: genName.trim(),
        agent_type: genType,
        description: genDescription.trim() || undefined,
        operator_id: operatorId,
        domains: parseDomains(genDomains),
      });
      // Order matters: hold the key and render it BEFORE anything else is
      // fired. If stall creation later fails, the operator is still
      // holding the only copy (spec §3.1).
      setAgent({ agent_id: res.agent_id, aex_id: res.aex_id, display_name: res.display_name });
      setMintedKey(res.private_key);
      setKeyAcked(false);
      setStep('key');
      onMutated();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const fetchChallenge = async () => {
    setChallengeBusy(true);
    setError(null);
    try {
      setChallenge(await api.getAuthChallenge());
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setChallengeBusy(false);
    }
  };

  const submitProof = async () => {
    if (!operatorId || !challenge) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.registerAgentExisting({
        aex_id: proofAexId.trim(),
        display_name: genName.trim(),
        agent_type: genType,
        description: genDescription.trim() || undefined,
        operator_id: operatorId,
        domains: parseDomains(genDomains),
        proof_of_ownership: {
          challenge: challenge.challenge,
          signature: proofSignature.trim(),
        },
      });
      setAgent({ agent_id: res.agent_id, aex_id: res.aex_id, display_name: res.display_name });
      setStep('stall');
      onMutated();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const useExistingAgent = () => {
    const found = ownedAgents.find((a) => a.agent_id === existingAgentId);
    if (!found) return;
    setAgent({ agent_id: found.agent_id, aex_id: null, display_name: found.display_name });
    setStep('stall');
  };

  const acknowledgeKey = () => {
    // The acknowledgement is the end of the key's life in this page.
    setMintedKey(null);
    setKeyAcked(false);
    setStep('stall');
  };

  const submitStall = async () => {
    if (!agent || visibility === null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createStall({
        name: stallName.trim(),
        description: stallDescription.trim() || undefined,
        agent_id: agent.agent_id,
        is_private: visibility === 'private',
      });
      // The 201 carries ids and the wallet only (stall-registry create), so
      // name and visibility come from what the operator chose; the verify
      // step then checks that choice against the server's own row.
      setStall({
        stall_id: res.stall_id,
        name: res.name ?? stallName.trim(),
        is_private: visibility === 'private',
      });
      setCertified(Boolean(res.is_certified));
      setLive(Boolean(res.is_live));
      setStep('lifecycle');
      onMutated();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doCertify = async () => {
    if (!stall) return;
    setBusy(true);
    setError(null);
    try {
      await api.certifyStall(stall.stall_id);
      setCertified(true);
      onMutated();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doGoLive = async () => {
    if (!stall || !certified) return;
    setBusy(true);
    setError(null);
    try {
      await api.goLiveStall(stall.stall_id);
      setLive(true);
      onMutated();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doFund = async () => {
    if (!stall) return;
    const amount = Number(fundAmount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.fundStall(stall.stall_id, amount);
      setFundSkipped(false);
      setStep('verify');
      onMutated();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runVerify = useCallback(async () => {
    if (!stall) return;
    setVerifyRunning(true);
    const items: VerifyItem[] = [];
    let row: ConsoleStall | undefined;
    try {
      const res = await api.getConsoleStalls();
      row = res.stalls.find((s) => s.stall_id === stall.stall_id);
    } catch (err) {
      items.push({ label: 'stall readable', ok: false, detail: errMessage(err) });
    }
    if (row) {
      items.push({ label: 'stall exists', ok: true, detail: row.stall_id });
      items.push({
        label: 'certified',
        ok: row.is_certified,
        detail: row.is_certified ? 'is_certified' : 'not certified — go back to step 3',
      });
      items.push({
        label: 'live',
        ok: row.is_live,
        detail: row.is_live ? 'is_live' : 'not live — go back to step 3',
      });
      items.push({
        label: 'worker agent bound',
        ok: Boolean(row.agent_id) && (!agent || row.agent_id === agent.agent_id),
        detail: row.agent_id ?? 'no agent_id on stall',
      });
      items.push({
        label: 'visibility as chosen',
        ok: row.is_private === stall.is_private,
        detail: row.is_private ? 'private — an internal seat' : 'public — a product',
      });
      items.push({
        label: 'funded',
        ok: row.wallet_balance > 0 ? true : fundSkipped ? null : false,
        detail:
          row.wallet_balance > 0
            ? `wallet ${row.wallet_balance} AEX`
            : 'wallet empty — Fund from the Stalls tab when the stall will spend',
      });
    } else {
      items.push({ label: 'stall exists', ok: false, detail: 'not in the console stalls read' });
    }

    // The spec §4 acceptance check, run as the anonymous public would see it.
    const probe = await probeStallPageAnonymous(stall.stall_id);
    if (stall.is_private) {
      items.push({
        label: 'anonymous page probe (private)',
        ok: probe.status !== 200,
        detail:
          probe.status !== 200
            ? `GET …/page → ${probe.status || 'unreachable'} for anonymous callers, as chosen`
            : 'PRIVATE STALL ANSWERS 200 UNAUTHENTICATED — visibility did not land',
      });
    } else {
      items.push({
        label: 'anonymous page probe (public)',
        ok: probe.status === 200,
        detail:
          probe.status === 200
            ? 'GET …/page → 200 unauthenticated — the listing is really public'
            : `GET …/page → ${probe.status || 'unreachable'} — a public stall the public cannot see`,
      });
    }

    setVerifyItems(items);
    setVerifyRunning(false);
  }, [api, stall, agent, fundSkipped]);

  useEffect(() => {
    if (step === 'verify' && !verifyRan.current) {
      verifyRan.current = true;
      void runVerify();
    }
  }, [step, runVerify]);

  // ---- Renderers -----------------------------------------------------------

  const renderSteps = () => (
    <div className="onboarding-steps">
      {STEP_ORDER.map((s, i) => (
        <span
          key={s}
          className={`onboarding-step${stepIndex(step) === i ? ' active' : ''}${stepIndex(step) > i ? ' done' : ''}`}
        >
          {STEP_LABELS[s]}
        </span>
      ))}
    </div>
  );

  const renderIdentity = () => (
    <>
      <div className="onboarding-mode-row" role="radiogroup" aria-label="Identity path">
        <label className="onboarding-mode">
          <input
            type="radio"
            name="identity-mode"
            checked={identityMode === 'generate'}
            onChange={() => setIdentityMode('generate')}
          />
          <span>Generate a new identity</span>
        </label>
        <label className="onboarding-mode">
          <input
            type="radio"
            name="identity-mode"
            checked={identityMode === 'existing'}
            onChange={() => setIdentityMode('existing')}
          />
          <span>Use an agent I already registered</span>
        </label>
        <label className="onboarding-mode">
          <input
            type="radio"
            name="identity-mode"
            checked={identityMode === 'proof'}
            onChange={() => setIdentityMode('proof')}
          />
          <span>Bring my own AEX identity (signed proof)</span>
        </label>
      </div>

      {identityMode !== 'existing' && (
        <div className="onboarding-fields">
          <label className="onboarding-field">
            <span>Display name</span>
            <input
              type="text"
              value={genName}
              onChange={(e) => setGenName(e.target.value)}
              placeholder="Barry"
              disabled={busy}
            />
          </label>
          <label className="onboarding-field">
            <span>Agent type</span>
            <select value={genType} onChange={(e) => setGenType(e.target.value as AgentType)} disabled={busy}>
              {AGENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className="onboarding-field">
            <span>Domains (comma-separated, optional)</span>
            <input
              type="text"
              value={genDomains}
              onChange={(e) => setGenDomains(e.target.value)}
              placeholder="sports_intelligence, prediction"
              disabled={busy}
            />
          </label>
          <label className="onboarding-field">
            <span>Description (optional)</span>
            <input
              type="text"
              value={genDescription}
              onChange={(e) => setGenDescription(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
      )}

      {identityMode === 'generate' && (
        <>
          <div className="onboarding-note">
            The server mints an Ed25519 keypair. The private key comes back once, in the next
            screen, and is never stored anywhere by this console — have somewhere safe ready
            before you continue.
          </div>
          <div className="onboarding-actions">
            <button
              type="button"
              className="action-btn primary"
              onClick={submitGenerate}
              disabled={busy || !genName.trim() || !operatorId}
            >
              {busy ? 'Registering…' : 'Register agent'}
            </button>
          </div>
        </>
      )}

      {identityMode === 'existing' && (
        <>
          <div className="onboarding-note">
            Resuming after an interruption? The agent you already minted is in this list —
            picking it here is how a resume never mints a second orphan identity.
          </div>
          <div className="onboarding-fields">
            <label className="onboarding-field">
              <span>Agent</span>
              <select
                value={existingAgentId}
                onChange={(e) => setExistingAgentId(e.target.value)}
                disabled={busy}
              >
                <option value="">— pick an agent —</option>
                {ownedAgents.map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.display_name} ({a.agent_id.slice(0, 8)}…)
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="onboarding-actions">
            <button
              type="button"
              className="action-btn primary"
              onClick={useExistingAgent}
              disabled={busy || !existingAgentId}
            >
              Continue with this agent
            </button>
          </div>
        </>
      )}

      {identityMode === 'proof' && (
        <>
          <div className="onboarding-note">
            Paste a <strong>signed proof</strong>, never a private key. Fetch a challenge,
            sign it on your own machine with your AEX key, and paste the hex signature.
            The challenge is hex: sign the bytes it decodes to, not the text of the hex
            string (for example, in Node:{' '}
            <span className="mono">crypto.sign(null, Buffer.from(challenge, &apos;hex&apos;), key)</span>).
            Your aex_id is <span className="mono">did:aex:</span> followed by the base58 of your
            32-byte Ed25519 public key. Anything that asks you to paste the key
            itself is a surface you should not trust — including this one, if it ever did.
          </div>
          <div className="onboarding-fields">
            <label className="onboarding-field">
              <span>aex_id</span>
              <input
                type="text"
                value={proofAexId}
                onChange={(e) => setProofAexId(e.target.value)}
                placeholder="did:aex:…"
                disabled={busy}
              />
            </label>
            <div className="onboarding-field">
              <span>Challenge</span>
              {challenge ? (
                <div className="onboarding-challenge mono">
                  {challenge.challenge}
                  <div className="onboarding-muted">expires {new Date(challenge.expires_at).toLocaleTimeString()}</div>
                </div>
              ) : (
                <button
                  type="button"
                  className="action-btn ghost"
                  onClick={fetchChallenge}
                  disabled={challengeBusy}
                >
                  {challengeBusy ? 'Fetching…' : 'Fetch challenge'}
                </button>
              )}
            </div>
            <label className="onboarding-field">
              <span>Signature (hex)</span>
              <input
                type="text"
                value={proofSignature}
                onChange={(e) => setProofSignature(e.target.value)}
                disabled={busy || !challenge}
              />
            </label>
          </div>
          <div className="onboarding-actions">
            <button
              type="button"
              className="action-btn primary"
              onClick={submitProof}
              disabled={busy || !genName.trim() || !proofAexId.trim() || !proofSignature.trim() || !challenge || !operatorId}
            >
              {busy ? 'Verifying…' : 'Register with proof'}
            </button>
          </div>
        </>
      )}
    </>
  );

  const renderKey = () =>
    mintedKey === null ? null : (
      <div className="onboarding-key-panel" role="alertdialog" aria-label="Save your private key">
        <div className="onboarding-key-warning">
          This is the <strong>only copy</strong> of this agent&apos;s private key that will ever
          exist. It is not stored by AEXgora or by this console. If you lose it, the agent can
          never authenticate again; if you leak it, anyone can speak as this stall.
        </div>
        <div className="onboarding-key-value mono" data-testid="minted-private-key">{mintedKey}</div>
        {agent?.aex_id && (
          <div className="onboarding-muted mono">aex_id: {agent.aex_id}</div>
        )}
        <label className="onboarding-key-ack">
          <input
            type="checkbox"
            checked={keyAcked}
            onChange={(e) => setKeyAcked(e.target.checked)}
          />
          <span>I have saved this key somewhere safe. It will not be shown again.</span>
        </label>
        <div className="onboarding-actions">
          <button
            type="button"
            className="action-btn primary"
            onClick={acknowledgeKey}
            disabled={!keyAcked}
          >
            Continue — the key disappears from this page
          </button>
        </div>
      </div>
    );

  const renderStall = () => (
    <>
      {agent && (
        <div className="onboarding-muted">
          Worker agent: <span className="mono">{agent.display_name || agent.agent_id}</span>
        </div>
      )}
      <div className="onboarding-fields">
        <label className="onboarding-field">
          <span>Stall name</span>
          <input
            type="text"
            value={stallName}
            onChange={(e) => setStallName(e.target.value)}
            placeholder="Diddja"
            disabled={busy}
          />
        </label>
        <label className="onboarding-field">
          <span>Description (optional)</span>
          <input
            type="text"
            value={stallDescription}
            onChange={(e) => setStallDescription(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>
      <div className="onboarding-visibility" role="radiogroup" aria-label="Visibility — required">
        <div className="onboarding-visibility-label">Visibility — choose one. There is no default.</div>
        <label className={`onboarding-visibility-option${visibility === 'public' ? ' selected' : ''}`}>
          <input
            type="radio"
            name="stall-visibility"
            checked={visibility === 'public'}
            onChange={() => setVisibility('public')}
          />
          <span>
            <strong>Public — a product.</strong> Anyone can find and approach this stall.
          </span>
        </label>
        <label className={`onboarding-visibility-option${visibility === 'private' ? ' selected' : ''}`}>
          <input
            type="radio"
            name="stall-visibility"
            checked={visibility === 'private'}
            onChange={() => setVisibility('private')}
          />
          <span>
            <strong>Private — an internal seat.</strong> Only you and your other seats can see it.
          </span>
        </label>
      </div>
      <div className="onboarding-actions">
        <button
          type="button"
          className="action-btn primary"
          onClick={submitStall}
          disabled={busy || !stallName.trim() || visibility === null || !agent}
        >
          {busy ? 'Creating…' : 'Create stall'}
        </button>
      </div>
    </>
  );

  const renderLifecycle = () => (
    <>
      {stall && (
        <div className="onboarding-muted">
          Stall <span className="mono">{stall.name}</span> ·{' '}
          {stall.is_private ? 'private' : 'public'} · <span className="mono">{stall.stall_id}</span>
        </div>
      )}
      <div className="onboarding-lifecycle-row">
        <button
          type="button"
          className="action-btn primary"
          onClick={doCertify}
          disabled={busy || certified}
        >
          {certified ? 'Certified ✓' : busy ? 'Working…' : 'Certify'}
        </button>
        <button
          type="button"
          className="action-btn primary"
          onClick={doGoLive}
          disabled={busy || !certified || live}
          title={certified ? undefined : 'Certify first — go-live refuses an uncertified stall'}
        >
          {live ? 'Live ✓' : busy ? 'Working…' : 'Go live'}
        </button>
        {!certified && (
          <span className="onboarding-muted">
            Go-live is disabled until the stall is certified — the order is enforced, not advisory.
          </span>
        )}
      </div>
      <div className="onboarding-actions">
        <button
          type="button"
          className="action-btn primary"
          onClick={() => setStep('fund')}
          disabled={!certified || !live}
        >
          Continue to funding
        </button>
      </div>
    </>
  );

  const renderFund = () => (
    <>
      <div className="onboarding-note">
        Fund the stall&apos;s wallet only if it will spend (post escrowed jobs, hire). A
        content-only listing can skip this and fund later from the Stalls tab.
      </div>
      <div className="onboarding-fields">
        <label className="onboarding-field">
          <span>Amount (AEX)</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            max="100000"
            value={fundAmount}
            onChange={(e) => setFundAmount(e.target.value)}
            disabled={busy}
            placeholder="25.00"
          />
        </label>
      </div>
      <div className="onboarding-actions">
        <button
          type="button"
          className="action-btn primary"
          onClick={doFund}
          disabled={busy || !(Number(fundAmount) > 0)}
        >
          {busy ? 'Funding…' : 'Fund stall'}
        </button>
        <button
          type="button"
          className="action-btn ghost"
          onClick={() => {
            setFundSkipped(true);
            setStep('verify');
          }}
          disabled={busy}
        >
          Skip — fund later
        </button>
      </div>
    </>
  );

  const renderVerify = () => (
    <>
      <div className="onboarding-note">
        The done-check — the console face of <span className="mono">mint-verify.mjs</span>:
        each item verifies from a real surface, and the flow is not done until every row is ✓.
      </div>
      {verifyRunning && <div className="loading">Running checks…</div>}
      {verifyItems && (
        <table className="wallet-table onboarding-verify-table">
          <tbody>
            {verifyItems.map((item) => (
              <tr key={item.label}>
                <td className={item.ok === false ? 'verify-bad' : item.ok === null ? 'verify-skip' : 'verify-ok'}>
                  {item.ok === null ? '—' : item.ok ? '✓' : '✗'}
                </td>
                <td>{item.label}</td>
                <td className="onboarding-muted">{item.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {verifyItems && verifyItems.some((i) => i.ok === false) && (
        <div className="auth-error" role="alert">
          Not done — at least one check failed. Fix it and re-run; a listing that fails its
          own probe is not listed, whatever the buttons said.
        </div>
      )}
      {verifyItems && !verifyItems.some((i) => i.ok === false) && !verifyRunning && (
        <div className="onboarding-done" role="status">
          All checks passed{verifyItems.some((i) => i.ok === null) ? ' (skipped items noted)' : ''} —
          the tenant is onboarded.
        </div>
      )}
      <div className="onboarding-actions">
        <button type="button" className="action-btn ghost" onClick={runVerify} disabled={verifyRunning}>
          Re-run checks
        </button>
        <button type="button" className="action-btn primary" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  );

  return (
    <section className="ops-panel onboarding-panel" aria-label="New tenant onboarding">
      <div className="onboarding-header">
        <h3 className="agent-section-header">New tenant</h3>
        <button type="button" className="action-btn ghost" onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
      {renderSteps()}
      {error && <div className="auth-error" role="alert">{error}</div>}
      {step === 'identity' && renderIdentity()}
      {step === 'key' && renderKey()}
      {step === 'stall' && renderStall()}
      {step === 'lifecycle' && renderLifecycle()}
      {step === 'fund' && renderFund()}
      {step === 'verify' && renderVerify()}
    </section>
  );
};

export default TenantOnboardingFlow;
